import { ChangeDetectorRef, Component, EventEmitter, Input, OnInit, Output, ViewChild, inject } from '@angular/core';
import {
    CdkDragDrop,
    CdkDragEnter,
    CdkDragMove,
    CdkDragStart,
    moveItemInArray,
    transferArrayItem
} from '@angular/cdk/drag-drop';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {fromEvent, Observable, of, range, throwError} from 'rxjs';
import {catchError, filter, finalize, map, take, tap} from 'rxjs/operators';

import {FeatureFlags, FeatureFlagService} from '@core';
import {NotificationService, TableComponent, TimeService, TransportType} from '@shared';

import {
    DragDropService,
    EmployeeService,
    EmployeeView,
    ManualRouteInformation,
    ProblemInstanceService, RouteKeyNumbers,
    RoutePlanningService, RoutesResult,
    RoutesService,
    TaskIdInfo,
    TaskView,
    Visit,
    VisitList
} from 'planner-shared';

import {RouteTaskView} from './route-task-view.model';
import {RouteTaskService} from './services/route-task.service';
import {RouteVisualizationService} from "./services/route-visualization.service";
import {CdkVirtualScrollViewport} from "@angular/cdk/scrolling";
import {TaskColorService} from "../../shared/services/task-color/task-color.service";
import {RouteDelayService} from "./services/route-delay.service";
import {DelayLevel} from "../models/delay-level.model";
import {TranslateService} from "@ngx-translate/core";

@UntilDestroy()
@Component({
    selector: 'app-route-planning-tasks',
    templateUrl: './route-planning-tasks.component.html',
    styleUrls: ['./route-planning-tasks.component.scss'],
    standalone: false
})
export class RoutePlanningTasksComponent implements OnInit {
    private readonly changeDetectorRef = inject(ChangeDetectorRef);
    private readonly dragDropService = inject(DragDropService);
    private readonly employeeService = inject(EmployeeService);
    private readonly routeTaskService = inject(RouteTaskService);
    private readonly routePlanningService = inject(RoutePlanningService);
    private readonly routesService = inject(RoutesService);
    private readonly featureFlagService = inject(FeatureFlagService);
    private readonly problemInstanceService = inject(ProblemInstanceService);
    private readonly routeVisualizationService = inject(RouteVisualizationService);
    private readonly timeService = inject(TimeService);
    private readonly taskColorService = inject(TaskColorService);
    private readonly routeDelayService = inject(RouteDelayService);
    private readonly translateService = inject(TranslateService);
    private readonly notificationService = inject(NotificationService);



    @Input()
    public set visits(value: Visit[]) {
        if (value) {
            if (this.tableComponent) {
                this.tableComponent.resetScrollPosition();
            }
            this.routeTaskService.mapToRouteTasks(value).subscribe(routeTasks => {
                this.routeTasks = routeTasks;
                if (routeTasks.length > 0) {
                    if (this.employee) {
                        this.loadTravelTimeVisualization();
                    }
                    if (routeTasks.filter(task => task.isSynced).length > 0) {
                        this.loadSyncedTasksDetails();
                    }
                    this.calculateFreeTime(routeTasks);
                    this.taskColorService.getTaskColors().forEach(coloredTask => {
                        const uncoloredTask =
                            this.routeTasks.find(routeTask => routeTask.taskId === coloredTask.taskId);
                        if (uncoloredTask) {
                            uncoloredTask.color = coloredTask.color;
                        }
                    });
                    if (this.pdfView) {
                        this.featureFlagService.isEnabled(FeatureFlags.HighlightDelays)
                            .pipe(
                                take(1),
                                untilDestroyed(this),
                                filter(isEnabled => isEnabled)
                            )
                            .subscribe(isEnabled => {
                                this.highlightDelayVisualizationEnabled = isEnabled;
                                this.loadRouteTaskDelays();
                            });
                    }
                    if (this.highlightDelayVisualizationEnabled && !this.pdfView) {
                        this.loadRouteTaskDelays();
                    }
                }
            });
        }
    }

    @Input() public set employeeKeyNumbers(employeeKeyNumbers: RouteKeyNumbers) {
        this.loadEndLocationTravelTime(employeeKeyNumbers);
    }

    @Input() public employee: EmployeeView;
    @Input() public pdfView: boolean;
    @Input() public saving: boolean;
    @Input() public dragDisabled: boolean;
    @Input() public routeSolution: Visit[];
    @ViewChild(CdkVirtualScrollViewport) virtualScrollViewport;

    @Output() public reloadRoutes = new EventEmitter<VisitList>();

    public transportTypes = TransportType;
    public routeTasks: RouteTaskView[];
    public draggingTask: boolean;
    public updating: boolean;
    public restrictionId: string;
    public showRestrictionContextMenu: boolean;
    public showRestrictionContextMenuTopPosition: number;
    public showRestrictionContextMenuLeftPosition: number;
    public showFreeTimeBetweenTasksFeature: Observable<boolean>;
    public showTravelTimeBackToOffice: Observable<boolean>;
    public showRoster: boolean;
    public officeAddress: string;
    public addressToggleValue: boolean;
    public rideShareIndexes: number[][];
    public isRideSharingEnabled: boolean;
    public endLocationArrivalName: Observable<string>;
    public endLocationTravelTime: string;
    public sortingDisabled = true;
    public highlightDelayVisualizationEnabled: boolean;
    public independentSyncTasksFeatureEnabled: boolean;
    public syncedTooltips = new Map<string, string>;

    private isDragAndDropFixEnabled: boolean;
    protected readonly DelayLevel = DelayLevel;
    @ViewChild(TableComponent, {static: false}) private readonly tableComponent: TableComponent;

    ngOnInit(): void {
        this.showRestrictionContextMenu = false;
        this.routePlanningService
            .getEmployeeTaskUpdateStateChange()
            .pipe(untilDestroyed(this))
            .subscribe(updating => (this.updating = updating));

        this.dragDropService
            .draggingTask()
            .pipe(untilDestroyed(this))
            .subscribe(task => (this.draggingTask = task !== null && task.workHourId !== this.employee.workHourId));
        this.showFreeTimeBetweenTasksFeature = this.featureFlagService.isEnabled(FeatureFlags.FreeTimeBetweenTasks);
        this.showTravelTimeBackToOffice = this.featureFlagService.isEnabled(FeatureFlags.TravelTimeToOffice);
        this.problemInstanceService.rosterDepartmentIsPartOfInstance()
            .pipe(untilDestroyed(this))
            .subscribe(result => this.showRoster = result.valueOf());
        this.problemInstanceService.requestProblemInstance()
            .pipe(untilDestroyed(this))
            .subscribe(instance => {
                this.officeAddress = instance.problemInstance.officeInfo.streetAddressNameNumberAndLetter;
                this.isRideSharingEnabled = instance.problemInstance.officeInfo.useRideSharingForOptimization;
                this.loadTravelTimeVisualization();
            });
        this.dragDropService.isDraggingDisabled().subscribe(value => this.dragDisabled = value);

        this.featureFlagService.isEnabled(FeatureFlags.LastStepDragFix)
            .subscribe(value => this.isDragAndDropFixEnabled = value);

        if (!this.pdfView) {
            this.featureFlagService.isEnabled(FeatureFlags.HighlightDelays)
                .pipe(
                    untilDestroyed(this),
                    take(1),
                    tap(isEnabled => {
                        this.highlightDelayVisualizationEnabled = isEnabled;
                        if (isEnabled) {
                            this.routeDelayService.getRouteDelays()
                                .pipe(untilDestroyed(this))
                                .subscribe(() => {
                                    if (this.routeTasks.length > 0) {
                                        this.loadRouteTaskDelays();
                                    }
                                });
                        }
                    })
                )
                .subscribe();
        }

        this.featureFlagService.isEnabled(FeatureFlags.IndependentSyncTasks)
            .pipe(
                untilDestroyed(this),
                take(1),
                tap(flagValue => {
                    this.independentSyncTasksFeatureEnabled = flagValue;
                })
            )
            .subscribe();

        fromEvent(document, 'click')
            .pipe(untilDestroyed(this))
            .subscribe(() => {
                this.showRestrictionContextMenu = false;
            });
    }

    public taskDropped(event: CdkDragDrop<RouteTaskView[]>): void {
        if (this.employee.dropDisabled && event.previousContainer !== event.container) {
            this.dragDropService.allowDragDrop(event);
            this.dragDropService.setDraggingDisabled(false);
            return;
        }

        if (
            event.previousContainer === event.container &&
            (!event.container.sortingDisabled || this.isDragAndDropFixEnabled) &&
            event.previousIndex !== event.currentIndex
        ) {
            const taskAtPreviousIndex = event.container.data[event.previousIndex];
            if (
                !taskAtPreviousIndex ||
                taskAtPreviousIndex.taskId !== event.item.data.taskId ||
                taskAtPreviousIndex.routePlannerTaskId !== event.item.data.routePlannerTaskId
            ) {
                // The bound task list changed while the task was mid-drag (a route reload or an
                // optimistic update replaced/mutated the array), so the drop indices no longer
                // describe the dragged task. Reordering now would send a taskList that does not
                // contain the moved task, which the backend rejects (MRP 400).
                this.notificationService.warning('TASK_LIST_CHANGED_WHILE_DRAGGING');
                this.dragDropService.setDraggingDisabled(false);
                return;
            }
            moveItemInArray(event.container.data, event.previousIndex, event.currentIndex);
            const tasks = event.container.data.map(
                i => <TaskIdInfo>{taskId: i.taskId, routePlannerTaskId: i.routePlannerTaskId}
            );

            this.updateRoutesOrderMrp(event, this.employee.employeeId, this.employee.workHourId, tasks, () =>
                moveItemInArray(event.container.data, event.currentIndex, event.previousIndex)
            ).subscribe(routes =>
                this.routesService.updateAndVerifyResult(routes, event, () => this.reloadRoutes.emit(routes), false)
            );
        } else if (event.previousContainer !== event.container) {
            transferArrayItem(
                event.previousContainer.data,
                event.container.data,
                event.previousIndex,
                event.currentIndex
            );

            this.updateRoutesOrderMrp(event, this.employee.employeeId, this.employee.workHourId, null, () =>
                transferArrayItem(
                    event.container.data,
                    event.previousContainer.data,
                    event.currentIndex,
                    event.previousIndex
                )
            ).subscribe(routes =>
                this.routesService.updateAndVerifyResult(routes, event, () => this.reloadRoutes.emit(routes), false)
            );
        } else {
            this.dragDropService.setDraggingDisabled(false);
        }
    }

    public entered(event: CdkDragEnter<RouteTaskView[]>): void {
        if (event.container !== event.item.dropContainer) {
            this.dragDropService.hidePlaceholder(event);
        }
    }

    public started(event: CdkDragStart<TaskView>): void {
        this.sortingDisabled = false;
        this.dragDropService.setDraggingDisabled(true);
        this.dragDropService.setDraggingTask(event.source.data);
        this.changeDetectorRef.detectChanges();
    }

    public released(): void {
        this.sortingDisabled = true;
        this.dragDropService.setDraggingTask(null);
    }

    public openModalWindow(task: RouteTaskView, e): void {
        e.preventDefault();
        this.showRestrictionContextMenu = true;
        this.restrictionId = task.originalTaskId;
    }

    public getMousePosition(e): void {
        this.showRestrictionContextMenuTopPosition = e.layerY;
        this.showRestrictionContextMenuLeftPosition = e.layerX;
    }

    public openEmployeesRestrictionModal(): void {
        this.showRestrictionContextMenu = false;
        this.employeeService.openEmployeesRestrictionModal(this.restrictionId);
    }

    public getTaskFromAddress(taskIndex: number): string {
        return this.routeVisualizationService.getTaskFromAddress(taskIndex, this.routeTasks);
    }

    public getTaskToAddress(taskIndex): string {
        return this.routeVisualizationService.getTaskToAddress(taskIndex, this.routeTasks);
    }

    public addressToggle(): void {
        this.addressToggleValue = !this.addressToggleValue;
    }

    public getRideSharingCompanionsName(routeTask: RouteTaskView): string {
        return this.routeVisualizationService.getRideSharingCompanionsName(routeTask);
    }

    public showRideSharingTask(taskIndex: number): boolean {
        if (!this.isRideSharingEnabled || !this.routeTasks.some(routeTask => routeTask.rideSharingConfig)) {
            return false;
        }
        let hasRideSharing = false;
        this.rideShareIndexes.forEach(rideShare => {
            if (rideShare.includes(taskIndex) && taskIndex !== rideShare[0]) {
                hasRideSharing = true;
            }
        });
        return hasRideSharing;
    }

    public onDragMoved(event: CdkDragMove) {
        const rect = this.virtualScrollViewport.elementRef.nativeElement.getBoundingClientRect();
        const y = event.pointerPosition.y;
        const offset = this.virtualScrollViewport.measureScrollOffset();
        const STEP = 8;
        const ZONE = 25;

        if (y < rect.top + ZONE) {
            this.virtualScrollViewport.scrollToOffset(offset - STEP);
        } else if (y > rect.bottom - ZONE) {
            this.virtualScrollViewport.scrollToOffset(offset + STEP);
        }
    }

    public getTaskDelayTooltip(routeTask: RouteTaskView): string {
        const delay = routeTask.taskDelayMinutes;
        if (delay < 60) {
            return this.translateService.instant('TASK_DELAYED_TOOLTIP_SHORT', {minutes: delay});
        }

        const hours = Math.floor(delay / 60);
        const minutes = delay % 60;
        return this.translateService.instant('TASK_DELAYED_TOOLTIP', {hours, minutes});
    }

    private loadSyncTooltip(task: RouteTaskView): string {
        if (task.singleSyncedTask) {
            return this.translateService.instant("SYNCED_TASK_NOT_PAIRED_ICON_TOOLTIP");
        } else if (this.independentSyncTasksFeatureEnabled && !task.singleSyncedTask) {
            return this.translateService.instant("SYNCED_TASK_INDEPENDENT_PAIRED_TOOLTIP",
                {employeeName: task.syncedEmployeeName});
        } else if (!task.rideSharingConfig || !task.syncedEmployeeName) {
            return this.translateService.instant("TASK_SYNCED_INFO_DELETE_MRP");
        } else {
            return this.translateService
                .instant("TASK_SYNCED_NAME_INFO_DELETE_MRP", {employeeName: task.syncedEmployeeName});
        }
    }

    private loadRouteTaskDelays(): void {
        const employeeDelays = this.routeDelayService
            .getRouteDelaysForWorkshift(this.routeTasks[0].workHourId).employeeRouteTasks;

        const delayMap = new Map(
            employeeDelays.map(delay => [delay.taskId, {
                delay: delay.taskDelayLevel,
                delayMin: delay.taskDelayMinutes
            }])
        );

        this.routeTasks.forEach(task => {
            const delay = delayMap.get(task.routePlannerTaskId);
            if (delay) {
                task.taskDelayMinutes = delay.delayMin;
                task.taskDelaySeverity = delay.delay;
            }
        });
    }

    private loadEndLocationTravelTime(employeeKeyNumbers: RouteKeyNumbers): void {
        if (!this.pdfView) {
            this.endLocationArrivalName = of(employeeKeyNumbers.endLocationArrivalName);
            this.endLocationTravelTime =
                this.routeVisualizationService
                    .calculateTravelTimeToOffice(this.routeTasks, employeeKeyNumbers.endLocationArrivalTime);
        }
    }

    private loadSyncedTasksDetails(): void {
        this.problemInstanceService.getProblemInstance()
            .pipe(
                untilDestroyed(this),
                take(1),
                tap(instanceResult => {
                    this.routesService.getLocalRoutes(instanceResult.problemInstance.jobId)
                        .pipe(
                            untilDestroyed(this),
                            take(1),
                            tap(routesResult => {
                                this.loadSyncedTaskEmployeeNames(routesResult);
                            })
                        )
                        .subscribe();
                })
            ).subscribe();
    }

    private loadTravelTimeVisualization(): void {
        if (this.isRideSharingEnabled) {
            this.compactTasks();
            if (!this.pdfView) {
                if (this.routeTasks.find(routeTask => routeTask.rideSharingConfig)) {
                    this.loadRideSharingRoutes();
                    this.loadTravelTimeIcons();
                }
                this.routeTasks[0].showIcon = true;
            }
            this.routeTasks = this.routeVisualizationService
                .loadRideSharingTasksDescription(this.routeTasks, this.pdfView);
        }
    }

    private loadTravelTimeIcons(): void {
        this.routeTasks.forEach((routeTask, index) => {
            this.rideShareIndexes.forEach(rideShare => {
                if (rideShare.includes(index)) {
                    if (rideShare[1] === index) {
                        routeTask.showIcon = true;
                        routeTask.rideSharingIcon = true;
                    }
                } else if (index !== 0 && rideShare.includes(index - 1)) {
                    routeTask.showIcon = true;
                }
            });
        });
    }

    private loadRideSharingRoutes(): void {
        let rideSharingCounter = 0;
        let startingIndex = 0;
        this.rideShareIndexes = [];
        this.routeTasks.forEach((task, arrayIndex) => {
            if (task.rideSharingConfig) {
                rideSharingCounter++;
                if (rideSharingCounter === 1) {
                    startingIndex = arrayIndex;
                } else if (rideSharingCounter === 2) {
                    rideSharingCounter = 0;
                    const indexCount = arrayIndex - startingIndex;
                    const rideShareIndexes: number[] = [];
                    range(startingIndex, indexCount + 1)
                        .pipe(
                            take(indexCount + 1))
                        .subscribe(x => {
                            rideShareIndexes.push(x);
                        });
                    this.rideShareIndexes.push(rideShareIndexes);
                }
            }
        });
    }

    private compactTasks() {
        this.routeTasks = this.routeVisualizationService.compactRideSharingTasks(this.routeTasks, this.routeSolution);
    }

    private updateRoutesOrderMrp(
        event: CdkDragDrop<RouteTaskView[]>,
        employeeId: string,
        workHourId: string,
        taskList: TaskIdInfo[],
        onError: Function
    ): Observable<VisitList> {
        const taskIdInfo = <TaskIdInfo>{
            taskId: event.item.data.taskId,
            routePlannerTaskId: event.item.data.routePlannerTaskId
        };
        const manualRouteInformationList = [
            <ManualRouteInformation>{
                taskIdInfo,
                employeeId,
                workHourId,
                taskList
            }
        ];

        this.updating = true;
        this.routePlanningService.employeeTaskUpdateStateChanged(true);

        return this.routesService.sendTaskChangeToMrp(manualRouteInformationList).pipe(
            catchError(() => {
                onError();
                return throwError('Update routes order failed.');
            }),
            map(result => result.routes),
            finalize(() => {
                this.routePlanningService.employeeTaskUpdateStateChanged(false);
                this.updating = false;
                this.dragDropService.setDraggingDisabled(false);
            })
        );
    }

    private calculateFreeTime(routeTasks: RouteTaskView[]): void {
        routeTasks.forEach((routeTask, index) => {
            if (index > 0) {
                const taskFreeTime =
                    this.timeService
                        .timeDifference(routeTasks[index - 1].toTime, routeTask.fromTime) - routeTask.travelTimeFromPrev;
                routeTask.freeTimeBeforeTask = taskFreeTime < 0 ? 0 : taskFreeTime;
            }
        });
    }

    private loadSyncedTaskEmployeeNames(routesResult: RoutesResult): void {
        const employeeSyncedTasks
            = this.routeTasks.filter(routeTask => routeTask.isSynced && !routeTask.rideSharingConfig);

        this.employeeService.getEmployees().subscribe(employees => {
            const otherRoutesSyncVisits = this.routeTaskService
                .estimateRouteSyncTaskPairs(routesResult.routes.tasks)
                .filter(syncedTask => syncedTask.employeeId !== this.employee.employeeId);

            this.routeTaskService.mapToRouteTasks(otherRoutesSyncVisits).pipe(take(1),
                tap(otherRouteSyncedTasks => {
                    for (const employeeSyncedTask of employeeSyncedTasks) {
                        const anotherRouteSyncedTask = otherRouteSyncedTasks.find(routeTask =>
                            (routeTask.taskId === employeeSyncedTask.taskId ||
                                routeTask.name === employeeSyncedTask.name) &&
                            employeeSyncedTask.employeeId !== routeTask.employeeId
                        );

                        if (!anotherRouteSyncedTask) {
                            employeeSyncedTask.syncedEmployeeName = "";
                            if (this.independentSyncTasksFeatureEnabled) {
                                employeeSyncedTask.singleSyncedTask = true;
                            }

                        } else {
                            employeeSyncedTask.syncedEmployeeName =
                                employees.find(e => e.employeeId === anotherRouteSyncedTask.employeeId).employeeName;

                            if (this.independentSyncTasksFeatureEnabled) {
                                if (anotherRouteSyncedTask.fromTime !== employeeSyncedTask.fromTime ||
                                    anotherRouteSyncedTask.toTime !== employeeSyncedTask.toTime) {
                                    employeeSyncedTask.syncedTaskNotTimeSynced = true;
                                }
                            }

                        }
                        this.employeeService.updateEmployeeLocalParams(this.employee);
                    }
                }))
                .subscribe();

            this.populateSyncedTooltips(employeeSyncedTasks);
        });
    }

    private populateSyncedTooltips(syncedTasks: RouteTaskView[]): void {
        this.syncedTooltips.clear();
        syncedTasks.forEach(task => {
            this.syncedTooltips.set(task.taskId, this.loadSyncTooltip(task));
        });
    }
}
